import { type ComponentType, useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, LayoutGrid, MessageCircle, Plus, UserRound } from "lucide-react";
import { useBillingStatus } from "@/features/billing/api";
import { useChatsUnread } from "@/features/chats/api";
import { useUnreadNotifications } from "@/features/notifications/api";
import { cn } from "@/lib/cn";
import { prefersReducedMotion } from "@/motion/useReducedMotion";
import { useAuth } from "@/stores/auth";
import { useUI } from "@/stores/ui";
import { autoHidesTabBar, useTabBarVisible } from "./chrome";

interface Tab {
  id: "home" | "categories" | "sell" | "chats" | "account";
  to?: string;
  onPress?: () => void;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
  badge?: number;
  dot?: boolean;
  active: (path: string) => boolean;
}

const SCROLL_THRESHOLD = 8;
const IDLE_SHOW_MS = 1200;

/** Slides the bar away while scrolling down on browse screens; back when scrolling up, at the top, or after 1.2 s idle. */
function useHideOnScroll(enabled: boolean): boolean {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    setHidden(false);
    if (!enabled) return;
    let lastY = window.scrollY;
    let frame = 0;
    let idle = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        if (y <= SCROLL_THRESHOLD) setHidden(false);
        else if (delta > SCROLL_THRESHOLD) setHidden(true);
        else if (delta < -SCROLL_THRESHOLD) setHidden(false);
        if (Math.abs(delta) > SCROLL_THRESHOLD) lastY = y;
        window.clearTimeout(idle);
        idle = window.setTimeout(() => setHidden(false), IDLE_SHOW_MS);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
      window.clearTimeout(idle);
    };
  }, [enabled]);
  return hidden;
}

/** Account dot: a payment to finish, a trial running out, or unread notifications. */
function useAccountDot(authed: boolean, isVendor: boolean): boolean {
  const notes = useUnreadNotifications().data ?? 0;
  const billing = useBillingStatus(authed && isVendor).data;
  if (!authed) return false;
  const state = billing?.state;
  const pay = billing?.latest_payment?.status === "AWAITING_TRANSFER";
  const expiring =
    state === "GRACE" ||
    state === "TRIAL_EXPIRED" ||
    state === "EXPIRED" ||
    state === "REJECTED" ||
    (state === "TRIALING" && (billing?.days_left ?? 99) <= 3);
  return notes > 0 || pay || expiring;
}

/**
 * Phone tab bar (and the installed app below desktop width): Home · Categories · Sell · Chats · Account.
 * Glass, 64 px + the home-indicator inset; a glowing pill slides to the active tab. It isn't rendered on
 * chat rooms, checkout, payment/billing, forms, full-screen viewers or while the keyboard is open.
 */
export function MobileBottomNav() {
  const visible = useTabBarVisible();
  const { pathname } = useLocation();
  const authed = useAuth((s) => s.status === "authenticated");
  const isVendor = useAuth((s) => !!s.user?.is_vendor);
  const chatUnread = useChatsUnread().data?.total ?? 0;
  const setCategoriesOpen = useUI((s) => s.setCategoriesOpen);
  const categoriesOpen = useUI((s) => s.categoriesOpen);
  const accountDot = useAccountDot(authed, isVendor);
  const hidden = useHideOnScroll(visible && autoHidesTabBar(pathname));
  const navRef = useRef<HTMLUListElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const placed = useRef(false);

  const tabs: Tab[] = [
    { id: "home", to: "/", label: "Home", icon: Home, end: true, active: (p) => p === "/" },
    {
      id: "categories",
      onPress: () => setCategoriesOpen(true),
      label: "Categories",
      icon: LayoutGrid,
      active: (p) => categoriesOpen || p.startsWith("/c/"),
    },
    {
      id: "sell",
      to: isVendor ? "/vendor/listings/new" : "/sell",
      label: "Sell",
      icon: Plus,
      active: (p) => p.startsWith("/sell") || p.startsWith("/vendor/listings/new"),
    },
    {
      id: "chats",
      to: authed ? "/chats" : "/login?next=/chats",
      label: "Chats",
      icon: MessageCircle,
      badge: authed ? chatUnread : 0,
      active: (p) => p.startsWith("/chats") || p.startsWith("/messages"),
    },
    {
      id: "account",
      to: "/more",
      label: "Account",
      icon: UserRound,
      dot: accountDot,
      active: (p) => p.startsWith("/more") || p.startsWith("/account") || p.startsWith("/settings") || p.startsWith("/notifications"),
    },
  ];
  const activeIndex = tabs.findIndex((t) => t.active(pathname));
  const pillIndex = activeIndex === 2 ? -1 : activeIndex;

  useLayoutEffect(() => {
    const nav = navRef.current;
    const pill = pillRef.current;
    if (!nav || !pill) return;
    if (pillIndex < 0) {
      pill.style.opacity = "0";
      return;
    }
    const cell = nav.clientWidth / tabs.length;
    const x = cell * pillIndex + cell / 2 - pill.offsetWidth / 2;
    // First placement jumps; later moves slide (CSS transition on transform/opacity).
    pill.style.transition = !placed.current || prefersReducedMotion() ? "none" : "transform .25s cubic-bezier(.2,.8,.2,1), opacity .15s";
    pill.style.transform = `translateX(${x}px)`;
    pill.style.opacity = "1";
    placed.current = true;
  }, [pillIndex, tabs.length, visible]);

  if (!visible) return null;

  const body = (tab: Tab, active: boolean) => {
    const Icon = tab.icon;
    const raised = tab.id === "sell";
    return (
      <>
        <span
          className={cn(
            "relative grid place-items-center transition-transform group-active:scale-[0.92]",
            raised
              ? "-mt-6 size-14 rounded-full bg-gradient-to-br from-[#7C5CFF] to-[#22D3EE] text-white shadow-[0_4px_14px_rgb(var(--rgb-primary)/0.35)] ring-4 ring-void dark:shadow-[0_0_24px_rgb(var(--rgb-primary)/0.55)]"
              : "size-7",
          )}
        >
          <Icon
            className={cn(
              raised ? "size-7" : "size-[22px]",
              active && !raised && "dark:drop-shadow-[0_0_8px_rgb(var(--rgb-accent-cyan)/0.8)]",
            )}
          />
          {!!tab.badge && (
            <span className="tabular absolute -right-2.5 -top-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-cyan px-1 text-[10px] font-bold text-void">
              {tab.badge > 99 ? "99+" : tab.badge}
            </span>
          )}
          {tab.dot && (
            <span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-[var(--magenta-strong)] ring-2 ring-void" aria-hidden />
          )}
        </span>
        <span className="max-w-full truncate leading-none">{tab.label}</span>
        {!!tab.badge && <span className="sr-only">, {tab.badge} unread</span>}
        {tab.dot && <span className="sr-only">, needs your attention</span>}
      </>
    );
  };
  const cell = "group flex h-16 min-h-12 w-full min-w-12 flex-col items-center justify-center gap-1 px-1 text-[11px] font-medium";

  return (
    <nav
      data-bottom-bar
      role="navigation"
      aria-label="Main"
      className={cn(
        "app-chrome fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/90 pb-safe-b shadow-[0_-4px_16px_-8px_rgb(var(--rgb-fg)/0.12)] transition-transform duration-200 ease-out [backdrop-filter:blur(20px)] dark:bg-[rgba(11,14,23,0.72)] dark:shadow-[0_-8px_24px_-12px_rgb(var(--rgb-primary)/0.35)]",
        hidden && "translate-y-full",
      )}
    >
      <ul ref={navRef} className="relative mx-auto grid max-w-md grid-cols-5">
        <span
          ref={pillRef}
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 h-[3px] w-7 rounded-full bg-[var(--primary)] opacity-0 dark:bg-cyan dark:shadow-[0_0_12px_2px_rgb(var(--rgb-accent-cyan)/0.7)]"
        />
        {tabs.map((tab, i) => {
          const active = i === activeIndex;
          return (
            <li key={tab.id} className="min-w-0">
              {tab.to ? (
                <NavLink
                  to={tab.to}
                  end={tab.end}
                  aria-current={active ? "page" : undefined}
                  className={cn(cell, active ? "text-[var(--primary-text)] dark:text-cyan" : "text-ink-muted hover:text-ink")}
                >
                  {body(tab, active)}
                </NavLink>
              ) : (
                <button
                  type="button"
                  onClick={tab.onPress}
                  aria-current={active ? "page" : undefined}
                  className={cn(cell, active ? "text-[var(--primary-text)] dark:text-cyan" : "text-ink-muted hover:text-ink")}
                >
                  {body(tab, active)}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
